import React from "react";
import { useTranslation } from "react-i18next";
import { Fade, Tooltip } from "@mui/material";
import tailwindClassnames from "../common/TailwindClassnames";

type Props = {
  photosFmt: string;
  scopeLabel: string;
  detectionSummary: string;
  classificationSummary: string;
  pipelineHint: string;
  pipelineDisabled: boolean;
  onRunPipeline: () => void;
  /**
   * Why the pipeline is blocked — e.g. "an import or upload is already in
   * progress for this selection". Shown as a tooltip on the disabled button,
   * matching how each stage card explains its own blocked run button.
   */
  blockReason?: string;
};

/**
 * "What will run" summary + the "Run full pipeline" action (AKL-766) — sits
 * full-width below the three stage cards, above Run history.
 */
const PipelineSummaryPanel: React.FC<Props> = ({
  photosFmt,
  scopeLabel,
  detectionSummary,
  classificationSummary,
  pipelineHint,
  pipelineDisabled,
  onRunPipeline,
  blockReason,
}) => {
  const { t } = useTranslation();

  return (
    <aside
      aria-label={t("Processing summary")}
      className={`${tailwindClassnames.formSectionCard} mb-5 text-left`}
    >
      <h2 className="text-xs font-bold uppercase tracking-wider text-off-black-light dark:text-greyed-text mb-3">
        {t("What will run")}
      </h2>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-2 text-sm max-w-xl">
        <dt className="text-off-black-light dark:text-greyed-text">
          {t("Photos")}
        </dt>
        <dd className="m-0 font-bold tabular-nums text-primary-off-black dark:text-primary-off-white">
          {photosFmt}
        </dd>
        <dt className="text-off-black-light dark:text-greyed-text">
          {t("Scope")}
        </dt>
        <dd className="m-0 text-primary-off-black dark:text-primary-off-white">
          {scopeLabel}
        </dd>
        <dt className="text-off-black-light dark:text-greyed-text">
          {t("Detection")}
        </dt>
        <dd className="m-0 text-primary-off-black dark:text-primary-off-white">
          {detectionSummary}
        </dd>
        <dt className="text-off-black-light dark:text-greyed-text">
          {t("Classification")}
        </dt>
        <dd className="m-0 text-primary-off-black dark:text-primary-off-white">
          {classificationSummary}
        </dd>
      </dl>
      <div className="flex flex-col items-start gap-2.5 mt-4">
        <span className="text-xs text-off-black-light dark:text-greyed-text">
          {pipelineHint}
        </span>
        <Tooltip
          title={pipelineDisabled && blockReason ? blockReason : ""}
          placement="top"
          arrow
          slotProps={{ tooltip: { sx: { fontSize: "1em" } } }}
          TransitionComponent={Fade}
          TransitionProps={{ timeout: 600 }}
        >
          {/* A disabled <button> fires no mouse events, so MUI needs a
              non-disabled wrapper or the tooltip never opens. The span takes
              focus while the button is blocked so the reason is reachable by
              keyboard as well as by hover. */}
          <span
            className="inline-flex"
            tabIndex={pipelineDisabled && blockReason ? 0 : undefined}
          >
            <button
              type="button"
              className={
                pipelineDisabled
                  ? tailwindClassnames.disabledSaveButton
                  : tailwindClassnames.saveButton
              }
              style={{ marginLeft: 0 }}
              disabled={pipelineDisabled}
              onClick={onRunPipeline}
            >
              {t("Run full pipeline")}
            </button>
          </span>
        </Tooltip>
      </div>
      <div className="mt-3.5 pt-3.5 border-t border-off-white-dark dark:border-storm-blue-border text-xs text-off-black-light dark:text-greyed-text leading-relaxed">
        {t(
          "Runs are queued and usually start within a minute. You can keep working — progress appears in Run history.",
        )}
      </div>
    </aside>
  );
};

export default PipelineSummaryPanel;
